import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { WalletLifecycleStateEnum } from '@olv/ui-shared/models/wallet.model';
import { FormatCoinCurrencyPipe } from '@olv/ui-shared/pipes/format-coin-currency.pipe';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { coinIcon } from '@olv/ui-shared/utils/asset-balance.utils';

interface WalletCurrency {
  name: string;
  amount: number;
  displayPrecision: number;
}

export function walletCardHeight(wallet: { balance?: { currencyBalances?: unknown[] } }): number {
  const rows = wallet.balance?.currencyBalances?.length ?? 0;
  return rows ? 68 + rows * 22 : 60;
}

export function walletsByNetwork<T extends { networkType: number }>(wallets: T[]): T[][] {
  const rows = new Map<number, T[]>();
  wallets.forEach((wallet) => {
    const row = rows.get(wallet.networkType);
    if (row) row.push(wallet);
    else rows.set(wallet.networkType, [wallet]);
  });
  return [...rows.values()];
}

export interface WalletCardAmountRow {
  label: string;
  amount: number;
  highlight?: boolean;
}

@Component({
  selector: 'app-wallet-card',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, MatIconModule, FormatCoinCurrencyPipe],
  templateUrl: './wallet-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./wallet-card.component.scss'],
})
export class WalletCardComponent implements OnInit, OnChanges {
  private clipboard = inject(Clipboard);
  private cd = inject(ChangeDetectorRef);

  @ViewChild('walletName', { static: false }) walletNameElement!: ElementRef<HTMLElement>;
  isNameOverflowing = false;

  @Input() id = '';
  @Input() name = 'Wallet name';
  @Input() address = '';
  @Input() networkType?: number;
  @Input() currencies: WalletCurrency[] = [];
  @Input() risk?: string;
  @Input() riskColor?: string;
  @Input() riskTextColor?: string;
  @Input() iconType?: 'ethereum' | 'tron' | string;
  @Input() iconBg?: string;
  @Input() pending?: boolean = false;
  @Input() isActive?: boolean = true;
  @Input() isClickable?: boolean = true;
  @Input() showRiskLevel?: boolean = true;
  @Input() lifecycleState?: number;
  @Input() isServing?: boolean;
  @Input() compactAddress = false;

  // Replaces the currency list
  @Input() amountRows: WalletCardAmountRow[] = [];

  get showAmountRows(): boolean {
    return this.amountRows.length > 0;
  }

  private readonly lifecycleStates = ['active', 'capped', 'closed', 'serving'];
  private readonly metadata = inject(NetworkMetadataService);
  networkIconMissing = false;

  constructor() {
    this.metadata.load();
  }
  tooltipText = 'Copy';
  private _timeout!: ReturnType<typeof setTimeout>;
  ngOnInit(): void {
    if (!this.address) {
      this.address = 'TFWb91Eu...iQMsaize';
    }
  }

  ngOnChanges(): void {
    this.networkIconMissing = false;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const name = this.walletNameElement?.nativeElement;
      this.isNameOverflowing = !!name && name.scrollHeight > name.clientHeight;
      this.cd.detectChanges();
    });
  }

  /**
   * Gets a truncated version of the wallet address for display
   */
  getFormattedAddress(): string {
    const [head, tail] = this.compactAddress ? [7, 7] : [9, 14];
    if (!this.address || this.address.length <= head + tail + 3) return this.address;

    return `${this.address.substring(0, head)}...${this.address.substring(this.address.length - tail)}`;
  }

  /**
   * Copies the wallet address to clipboard
   */
  copyToClipboard(event: Event): void {
    event.stopPropagation();
    if (this.address) {
      this.clipboard.copy(this.address);
      this.tooltipText = 'Copied';
      clearTimeout(this._timeout);
      this._timeout = setTimeout(() => {
        this.tooltipText = 'Copy';
      }, 1500);
      // Could add a toast notification here
    }
  }

  get networkName(): string {
    if (this.metadata.error()) return '-';
    return this.networkType == null ? '' : (this.metadata.network(this.networkType)?.name ?? '');
  }

  get networkIcon(): string | null {
    const iconKey = this.networkType == null ? null : this.metadata.network(this.networkType)?.iconKey;
    return iconKey && !this.networkIconMissing ? coinIcon(iconKey) : null;
  }

  /**
   * Determines if the warning icon should be displayed.
   * Show for non-pending wallets with a defined risk level (low, medium, high).
   */
  showWarningIcon(): boolean {
    // Show only if NOT pending AND risk is low, medium, or high
    const riskLevel = this.risk?.toLowerCase() ?? ''; // Ensure risk is checked safely
    return !this.pending && ['notassesed', 'low', 'medium', 'high', 'severe'].includes(riskLevel);
  }

  riskToolTip(risk: string | undefined): string {
    if (risk === 'unknown') {
      return 'Unknown';
    } else if (risk === 'low') {
      return 'Low';
    } else if (risk === 'medium') {
      return 'Medium';
    } else if (risk === 'high') {
      return 'High';
    } else if (risk === 'severe') {
      return 'Severe';
    } else {
      return 'Not Assessed';
    }
  }

  get lifecycleBadge(): string | null {
    const state = this.lifecycleState == null ? null : WalletLifecycleStateEnum[this.lifecycleState]?.toLowerCase();
    return state && this.lifecycleStates.includes(state) ? state : null;
  }

  get showLifecycleBadge(): boolean {
    return !!this.isServing || !!this.lifecycleBadge;
  }

  get lifecycleTooltip(): string {
    if (this.isServing) {
      return 'Serving';
    }
    const state = this.lifecycleBadge;
    return state ? state.charAt(0).toUpperCase() + state.slice(1) : '';
  }

  ngOnDestroy() {
    clearTimeout(this._timeout);
  }
}
